import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { CONTACT } from "../../lib/data";
import { apiPost, API_AVAILABLE } from "../../lib/api";

const SERVICE_OPTIONS = [
  "Immobilienfotografie", "Architekturfotografie", "Interior / Exterior",
  "Immobilienvideos", "Drohnenaufnahmen", "3D-Touren / 360°",
  "Virtual Staging", "Bildbearbeitung", "Grundrisse 2D / 3D",
  "Baudokumentation", "Social-Media-Content", "Sonstiges / Beratung",
];

const PROPERTY_OPTIONS = [
  "Wohnung", "Einfamilienhaus", "Mehrfamilienhaus", "Neubau / Projekt",
  "Gewerbeobjekt", "Ferienwohnung / Airbnb", "Hotel / Restaurant",
  "Grundstück", "Sonstiges",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    property_type: "", location: "", service: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte fülle Name, E-Mail und Nachricht aus.");
      return;
    }
    if (!API_AVAILABLE) {
      toast.error("Das Online-Formular ist gerade nicht verfügbar. Bitte kontaktieren Sie uns direkt per Telefon oder E-Mail.");
      return;
    }
    setLoading(true);
    try {
      await apiPost("/contact", form);
      toast.success("Vielen Dank! Ihre Anfrage ist eingegangen. Ich melde mich zeitnah.");
      setForm({ name: "", email: "", phone: "", property_type: "", location: "", service: "", message: "" });
    } catch {
      toast.error("Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 md:py-32 bg-[#F8F5EF]">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5 reveal">
          <div className="flex items-center gap-3 mb-6">
            
            <span className="overline">Kontakt</span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-[72px] leading-[1.02] text-[#1A1A1A] tracking-tight">
            Lassen Sie uns <br />
            <span className="italic font-light">Ihr Projekt</span> <br />
            <span className="gold-text italic">besprechen</span>.
          </h2>
          <p className="mt-8 max-w-md text-[15px] leading-relaxed text-[#5A5A5A] font-light">
            Erstgespräch, konkreter Auftrag oder unverbindliche Beratung — schreiben
            Sie kurz, was Sie planen. Antwort innerhalb eines Werktages.
          </p>

          <div className="mt-12 space-y-6">
            <a href="tel:+4924212633703" data-testid="contact-phone-link" className="flex items-center gap-5 group">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FFA500] shrink-0">
                <Phone size={18} className="text-[#1A1A1A]" />
              </div>
              <div>
                <div className="overline text-[10px] text-[#5A5A5A]">Telefon</div>
                <div className="mt-1 text-[15px] text-[#1A1A1A] group-hover:text-[#FFA500] transition-colors">{CONTACT.phone}</div>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-5 group">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FFA500] shrink-0">
                <Mail size={18} className="text-[#1A1A1A]" />
              </div>
              <div>
                <div className="overline text-[10px] text-[#5A5A5A]">E-Mail</div>
                <div className="mt-1 text-[15px] text-[#1A1A1A] group-hover:text-[#FFA500] transition-colors">{CONTACT.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FFA500] shrink-0">
                <MapPin size={18} className="text-[#1A1A1A]" />
              </div>
              <div>
                <div className="overline text-[10px] text-[#5A5A5A]">Adresse</div>
                <div className="mt-1 text-[15px] text-[#1A1A1A]">
                  {CONTACT.street}, {CONTACT.zip} {CONTACT.city}
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 reveal bg-white p-8 md:p-12 border border-[#EAE6DE]"
        >
          <div className="flex items-baseline justify-between mb-8">
            <h3 className="font-display text-3xl md:text-4xl text-[#1A1A1A]">Projekt anfragen</h3>
            <span className="overline text-[10px] text-[#5A5A5A]">Antwort &lt; 24h</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <Field label="Name *" name="name" value={form.name} onChange={onChange} testid="contact-input-name" placeholder="Ihr vollständiger Name" />
            <Field label="E-Mail *" type="email" name="email" value={form.email} onChange={onChange} testid="contact-input-email" placeholder="ihre@email.de" />
            <Field label="Telefon" name="phone" value={form.phone} onChange={onChange} testid="contact-input-phone" placeholder="02421 ..." />
            <Field label="Standort der Immobilie" name="location" value={form.location} onChange={onChange} testid="contact-input-location" placeholder="z. B. Aachen, Köln, Düren" />
            <Select label="Art der Immobilie" name="property_type" value={form.property_type} onChange={onChange} options={PROPERTY_OPTIONS} testid="contact-select-property" />
            <Select label="Gewünschte Leistung" name="service" value={form.service} onChange={onChange} options={SERVICE_OPTIONS} testid="contact-select-service" />
          </div>

          <div className="mt-8">
            <label className="block overline text-[10px] mb-3">Nachricht *</label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              data-testid="contact-input-message"
              rows={5}
              className="w-full bg-transparent border-b border-[#1A1A1A]/25 focus:border-[#FFA500] outline-none text-[15px] text-[#1A1A1A] py-3 transition-colors font-light resize-none placeholder:text-[#9A9A95]"
              placeholder="Beschreiben Sie kurz Ihr Projekt"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="group mt-10 inline-flex items-center justify-between px-8 py-4 bg-[#1A1A1A] text-[#F8F5EF] text-xs uppercase tracking-[0.2em] hover:bg-[#FFA500] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-60 min-w-[260px] font-medium"
          >
            {loading ? "Wird gesendet…" : "Anfrage senden"}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
          </button>

          <p className="mt-6 text-xs text-[#5A5A5A] font-light">
            Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="block overline text-[10px] text-[#5A5A5A] mb-3">{label}</label>
      <input
        type={type} name={name} value={value} onChange={onChange}
        data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#1A1A1A]/25 focus:border-[#FFA500] outline-none text-[15px] text-[#1A1A1A] py-3 transition-colors font-light placeholder:text-[#9A9A95]"
      />
    </div>
  );
}

function Select({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block overline text-[10px] text-[#5A5A5A] mb-3">{label}</label>
      <select
        name={name} value={value} onChange={onChange} data-testid={testid}
        className="w-full bg-transparent border-b border-[#1A1A1A]/25 focus:border-[#FFA500] outline-none text-[15px] text-[#1A1A1A] py-3 transition-colors font-light cursor-pointer appearance-none"
      >
        <option value="">Bitte wählen</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
